import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Target, Sparkles, Tag, ShoppingBag, Package } from "lucide-react";
import { Button } from "../components/ui/button";
import { RequestCard } from "../components/RequestCard";
import { ProductCard } from "../components/ProductCard";
import { api } from "../lib/api";
import { markMatchesSeen } from "../components/MatchBadge";

export default function MatchesPage() {
  const navigate = useNavigate();
  const [data, setData] = useState({
    categories: [], requests: [],
    seller_categories: [], buyer_categories: [], products: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/me/matches");
        setData(res.data);
      } finally {
        setLoading(false);
      }
      // Clear unseen badge on visit
      markMatchesSeen();
    })();
  }, []);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const sellerCats = data.seller_categories || data.categories || [];
  const buyerCats = data.buyer_categories || [];
  const hasSellerSide = sellerCats.length > 0;
  const hasBuyerSide = buyerCats.length > 0;

  return (
    <div className="mx-auto max-w-[1400px] px-4 sm:px-6 md:px-10 py-6 sm:py-10 md:py-14">
      <button
        onClick={() => navigate(-1)}
        data-testid="back-btn"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6 sm:mb-8"
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </button>

      <div className="mb-8">
        <div className="inline-flex items-center gap-1.5 rounded-full bg-accent/10 text-accent px-3 py-1 text-[10px] tracking-[0.2em] uppercase font-bold mb-3">
          <Target className="h-3 w-3" />
          Best match feed
        </div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
          Matches tailored to you.
        </h1>
        <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl">
          New buyer requests in your product categories, and new products in the categories you're sourcing.
        </p>
      </div>

      {!hasSellerSide && !hasBuyerSide ? (
        <div className="rounded-md border-2 border-dashed border-border bg-white p-10 text-center">
          <Sparkles className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-sm text-muted-foreground mb-4">
            List a product or post a buy request to activate your Best Match feed.
          </p>
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <Button onClick={() => navigate("/new-product")} className="rounded-md" data-testid="activate-list-product">
              List a product
            </Button>
            <Button onClick={() => navigate("/new")} variant="outline" className="rounded-md" data-testid="activate-post-request">
              Post a request
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-14">
          {hasSellerSide && (
            <section data-testid="matches-seller-section">
              <SectionHeader
                icon={<Package className="h-3 w-3" />}
                kicker="For sellers"
                title="Buyers who need what you sell."
                subtitle="Open buy requests in the same categories as your product listings."
                categories={sellerCats}
              />
              {data.requests.length === 0 ? (
                <EmptyState icon={<Target className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" strokeWidth={1.5} />}
                  text="No open buy requests match your product categories right now." />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {data.requests.map((r, i) => <RequestCard key={r.id} req={r} index={i} />)}
                </div>
              )}
            </section>
          )}

          {hasBuyerSide && (
            <section data-testid="matches-buyer-section">
              <SectionHeader
                icon={<ShoppingBag className="h-3 w-3" />}
                kicker="For buyers"
                title="Products that match what you're sourcing."
                subtitle="Fresh listings in the categories of your open buy requests."
                categories={buyerCats}
              />
              {(data.products || []).length === 0 ? (
                <EmptyState icon={<ShoppingBag className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" strokeWidth={1.5} />}
                  text="No new products match your request categories right now." />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {data.products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
                </div>
              )}
            </section>
          )}
        </div>
      )}
    </div>
  );
}

function SectionHeader({ icon, kicker, title, subtitle, categories }) {
  return (
    <div className="mb-6">
      <div className="inline-flex items-center gap-1.5 rounded-full bg-foreground text-background px-3 py-1 text-[10px] tracking-[0.2em] uppercase font-bold mb-3">
        {icon}
        {kicker}
      </div>
      <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight leading-tight">{title}</h2>
      <p className="mt-2 text-sm text-muted-foreground max-w-2xl">{subtitle}</p>
      {categories.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {categories.map((c) => (
            <span key={c} className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1 text-xs font-semibold">
              <Tag className="h-3 w-3" />
              {c}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyState({ icon, text }) {
  return (
    <div className="rounded-md border-2 border-dashed border-border bg-white p-10 text-center">
      {icon}
      <p className="text-sm text-muted-foreground">{text}</p>
    </div>
  );
}
